import { api } from "./axios";
import type { Channel, Device, VideoTransport } from "../types";

export async function listDevices(): Promise<Device[]> {
  const r = await api.get("/devices");
  return r.data;
}

export async function getDevice(id: number): Promise<Device> {
  const r = await api.get(`/devices/${id}`);
  return r.data;
}

export interface DeviceCreate {
  name: string;
  host: string;
  http_port?: number;
  rtsp_port?: number;
  use_https?: boolean;
  video_transport?: VideoTransport;
  username: string;
  password: string;
  // 0 / omitido = autodetectar leyendo del NVR; >0 = limite manual.
  channels_count?: number;
  // Canales 1-based a excluir de la operacion.
  excluded_channels?: number[];
}

export async function createDevice(data: DeviceCreate): Promise<Device> {
  const r = await api.post("/devices", data);
  return r.data;
}

export async function updateDevice(id: number, data: Partial<DeviceCreate> & { is_enabled?: boolean }): Promise<Device> {
  const r = await api.patch(`/devices/${id}`, data);
  return r.data;
}

export async function deleteDevice(id: number): Promise<void> {
  await api.delete(`/devices/${id}`);
}

export async function testConnection(id: number) {
  const r = await api.post(`/devices/${id}/test-connection`);
  return r.data as { ok: boolean; message: string; info: Record<string, unknown> };
}

// Verificacion liviana de conectividad para la Guardia Virtual. Gateada con
// can_view_live en el backend (a diferencia de testConnection que exige
// can_manage_devices). Timeout amplio: el probe del DVR puede tardar ~12s.
export async function verifyDevice(id: number) {
  const r = await api.post(`/devices/${id}/verify`, null, { timeout: 20000 });
  return r.data as { ok: boolean; message: string; info: Record<string, unknown> };
}

export async function syncChannels(id: number) {
  const r = await api.post(`/devices/${id}/sync-channels`);
  return r.data as Array<{ channel: number; name: string }>;
}

export async function listDeviceChannels(id: number): Promise<Channel[]> {
  const r = await api.get(`/devices/${id}/channels`);
  return r.data;
}

// Trae TODOS los channels accesibles del backend en una sola request.
// Sustituye a 355+ requests paralelos de listDeviceChannels() en hooks que
// necesitan poblar el store completo (Live View, Playback selectores).
// El backend hace un solo SELECT a la tabla channels y devuelve la lista
// plana ordenada por device_id, channel_number.
export async function listAllChannels(): Promise<Channel[]> {
  const r = await api.get("/channels");
  return r.data;
}

export async function rebootDevice(id: number) {
  const r = await api.post(`/devices/${id}/reboot`);
  return r.data;
}

export async function getDeviceStatus(id: number) {
  const r = await api.get(`/devices/${id}/status`);
  return r.data;
}

export function snapshotUrl(id: number, channel: number): string {
  return `/api/v1/devices/${id}/snapshot/${channel}`;
}

// ---- Bulk import / export -------------------------------------------------

export interface BulkImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row?: number; host?: string; name?: string; error: string }>;
}

export async function bulkImportDevices(file: File): Promise<BulkImportResult> {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/devices/import", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data;
}

// ---- Bulk sync hora/zona/DST/NTP -----------------------------------------

import type { DstConfig, NtpConfig } from "./config";

export interface BulkSyncTimePayload {
  device_ids: number[];
  time?: string;
  timezone?: number;
  dst?: Partial<DstConfig>;
  ntp?: Partial<NtpConfig>;
}

export interface BulkSyncTimeResult {
  success: Array<{ device_id: number; name: string; applied: Record<string, unknown> }>;
  errors: Array<{ device_id: number; name: string | null; error: string }>;
}

// El server procesa los devices en paralelo (Semaphore 20) y responde al final.
// Timeout local en 0 (sin tope) como los otros bulk: con ~350 devices y algunos
// offline la corrida puede pasar de los 30 s por defecto (antes: 499 y "Error en
// el envio bulk" aunque el server terminaba de aplicar la hora igual).
export async function bulkSyncTime(payload: BulkSyncTimePayload): Promise<BulkSyncTimeResult> {
  const r = await api.post("/devices/sync-time", payload, { timeout: 0 });
  return r.data;
}

// ---- Bulk re-detectar canales con camara real ----------------------------

export interface DeviceRefreshResult {
  device_id: number;
  name: string;
  before: number;
  after: number;
  removed_channels: number[];
  added_channels: number[];
  skipped_reason?: string | null;
}

export interface BulkRefreshChannelsResult {
  updated: DeviceRefreshResult[];
  unchanged: DeviceRefreshResult[];
  errors: Array<{ device_id: number; name: string | null; error: string }>;
}

// Procesa N devices en paralelo en el server (Semaphore 30). El timeout local
// se pone en 0 (sin tope) porque devices VPN lentos pueden acumular hasta 12s
// cada uno y el total puede pasar de 30s con muchos offline.
export async function refreshChannelsBulk(deviceIds?: number[]): Promise<BulkRefreshChannelsResult> {
  const r = await api.post(
    "/devices/refresh-channels",
    { device_ids: deviceIds && deviceIds.length > 0 ? deviceIds : null },
    { timeout: 0 },
  );
  return r.data;
}

// ---- Bulk test-connection (refresca modelo/firmware/serial/online) -------

export interface DeviceTestResult {
  device_id: number;
  name: string;
  host: string;
  ok: boolean;
  model?: string | null;
  firmware?: string | null;
  serial?: string | null;
  message?: string;
}

export interface BulkTestConnectionResult {
  online: DeviceTestResult[];
  offline: DeviceTestResult[];
}

export async function testConnectionBulk(deviceIds?: number[]): Promise<BulkTestConnectionResult> {
  const r = await api.post(
    "/devices/test-connection-all",
    { device_ids: deviceIds && deviceIds.length > 0 ? deviceIds : null },
    { timeout: 0 },
  );
  return r.data;
}

// Descarga la lista de dispositivos como Excel (.xlsx). El archivo incluye
// las contrasenas en texto plano — tratarlo como confidencial. Si no hay
// dispositivos, el archivo sale con solo headers y sirve de plantilla.
export async function downloadDevicesXlsx(): Promise<void> {
  const r = await api.get("/devices/export.xlsx", { responseType: "blob" });
  const cd = (r.headers["content-disposition"] || "") as string;
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const fname = m?.[1] || "dispositivos.xlsx";
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement("a");
  a.href = url; a.download = fname;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
